import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, posterUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  CheckCircle2, XCircle, ShieldCheck, Users, Film, Clock, BadgeCheck, Banknote,
  Wrench, Power, Type, Trash2, RotateCcw, ShieldOff, ShieldAlert, Search,
  History, MessageSquare, RefreshCw, Stethoscope, UserPlus, Mail, ArrowUpCircle, ArrowDownCircle, Sparkles,
  GripVertical, ChevronUp, ChevronDown, Trophy,
} from "lucide-react";
import { useMaintenance } from "../context/MaintenanceContext";
import { useBranding } from "../context/BrandingContext";
import { Switch } from "../components/ui/switch";
import ConfirmDeleteModal from "../components/ConfirmDeleteModal";
import NewsletterAdmin from "../components/NewsletterAdmin";
import NotificationsPanel from "../components/NotificationsPanel";
import AdminEarnings from "../components/AdminEarnings";
import ConfirmSuspendModal from "../components/ConfirmSuspendModal";

export default function AdminPanel() {
  const { user: me } = useAuth();
  const [stats, setStats] = useState(null);
  const [pending, setPending] = useState([]);
  const [all, setAll] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [users, setUsers] = useState([]);
  const [channels, setChannels] = useState([]);
  const [appeals, setAppeals] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [tab, setTab] = useState("pending");
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState("");
  const [rejectingPayout, setRejectingPayout] = useState(null);
  const [payoutReason, setPayoutReason] = useState("");
  const [userQ, setUserQ] = useState("");
  const [channelQ, setChannelQ] = useState("");

  // Modal state (typed-confirm for hard deletes; suspend modal for soft actions)
  const [hardDelete, setHardDelete] = useState(null); // { kind, id, confirmText, label, description }
  const [softAction, setSoftAction] = useState(null); // { kind, id, title, description, confirmButtonLabel }
  const [modalBusy, setModalBusy] = useState(false);

  const { enabled: maintEnabled, message: maintMessage, refresh: refreshMaint } = useMaintenance();
  const [maintMsgDraft, setMaintMsgDraft] = useState("");
  const [maintBusy, setMaintBusy] = useState(false);

  // Scheduled maintenance window — admin-only fields.
  // Values in the local <input type="datetime-local"> format (YYYY-MM-DDTHH:MM).
  const [schedStart, setSchedStart] = useState("");
  const [schedEnd, setSchedEnd] = useState("");
  const [schedBusy, setSchedBusy] = useState(false);
  const [schedInfo, setSchedInfo] = useState(null);  // {start_at, end_at, notified_at, scheduled_by}

  // Live Leaderboard (Top-earning Films on /browse) toggle
  const [leaderboardEnabled, setLeaderboardEnabled] = useState(true);
  const [leaderboardBusy, setLeaderboardBusy] = useState(false);

  const {
    site_title, meta_description, og_title, og_description, og_image_url,
    refresh: refreshBranding,
  } = useBranding();
  const [titleDraft, setTitleDraft] = useState("");
  const [descDraft, setDescDraft] = useState("");
  const [ogTitleDraft, setOgTitleDraft] = useState("");
  const [ogDescDraft, setOgDescDraft] = useState("");
  const [ogImageDraft, setOgImageDraft] = useState("");
  const [brandBusy, setBrandBusy] = useState(false);

  useEffect(() => { setMaintMsgDraft(maintMessage || ""); }, [maintMessage]);
  useEffect(() => {
    setTitleDraft(site_title || "");
    setDescDraft(meta_description || "");
    setOgTitleDraft(og_title || "");
    setOgDescDraft(og_description || "");
    setOgImageDraft(og_image_url || "");
  }, [site_title, meta_description, og_title, og_description, og_image_url]);

  const isAppOwner = !!(me && me.is_app_owner);
  const [invites, setInvites] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteMessage, setInviteMessage] = useState("");
  const [inviteBusy, setInviteBusy] = useState(false);

  // Featured-slots reorder state
  const [featuredOrder, setFeaturedOrder] = useState([]);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const [reorderBusy, setReorderBusy] = useState(false);
  const [rotationSettings, setRotationSettings] = useState({ enabled: false, last_rotated_at: null });
  const [rotateBusy, setRotateBusy] = useState(false);

  // Genres tab state
  const [genres, setGenres] = useState([]);
  const [newGenreName, setNewGenreName] = useState("");
  const [genreBusy, setGenreBusy] = useState(false);
  const [editingGenreId, setEditingGenreId] = useState(null);
  const [editingGenreName, setEditingGenreName] = useState("");
  const [editingShelfId, setEditingShelfId] = useState(null);
  const [shelfTagline, setShelfTagline] = useState("");
  const [shelfIntro, setShelfIntro] = useState("");

  const refresh = async () => {
    const [s, p, a, po, us, ch, ap, al, fe, rot, gen] = await Promise.all([
      api.get("/admin/stats").then((r) => r.data).catch(() => null),
      api.get("/admin/movies/pending").then((r) => r.data).catch(() => []),
      api.get("/admin/movies").then((r) => r.data).catch(() => []),
      api.get("/admin/payouts").then((r) => r.data).catch(() => []),
      api.get("/admin/users").then((r) => r.data).catch(() => []),
      api.get("/admin/channels").then((r) => r.data).catch(() => []),
      api.get("/admin/appeals").then((r) => r.data).catch(() => []),
      api.get("/admin/audit-log?limit=200").then((r) => r.data).catch(() => []),
      api.get("/admin/movies/featured").then((r) => r.data).catch(() => []),
      api.get("/admin/settings/featured-rotation").then((r) => r.data).catch(() => ({ enabled: false, last_rotated_at: null })),
      api.get("/genres").then((r) => r.data).catch(() => []),
    ]);
    setStats(s); setPending(p); setAll(a); setPayouts(po);
    setUsers(us); setChannels(ch); setAppeals(ap); setAuditLog(al);
    setFeaturedOrder(fe || []);
    setRotationSettings(rot || { enabled: false, last_rotated_at: null });
    setGenres(gen || []);
    if (me && me.is_app_owner) {
      try { const { data } = await api.get("/admin/invites"); setInvites(data || []); } catch { setInvites([]); }
    }
  };

  useEffect(() => { refresh(); }, [me?._id, me?.is_app_owner]); // eslint-disable-line react-hooks/exhaustive-deps

  const moderate = async (id, status, rejection_reason = null) => {
    try {
      await api.post(`/admin/movies/${id}/moderate`, { status, rejection_reason });
      toast.success(status === "approved" ? "Approved" : "Rejected");
      setRejecting(null);
      setReason("");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const markPaid = async (id) => {
    try {
      await api.post(`/admin/payouts/${id}/mark-paid`);
      toast.success("Marked as paid");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const rejectPayout = async (id) => {
    if (!payoutReason.trim()) return toast.error("Reason required");
    try {
      await api.post(`/admin/payouts/${id}/reject`, { reason: payoutReason });
      toast.success("Payout rejected");
      setRejectingPayout(null);
      setPayoutReason("");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const toggleMaintenance = async (next) => {
    setMaintBusy(true);
    try {
      await api.post("/admin/maintenance", { enabled: next, message: maintMsgDraft });
      await refreshMaint();
      toast.success(next ? "Maintenance mode is ON — public access blocked" : "Maintenance mode is OFF — site is live");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setMaintBusy(false);
    }
  };

  const saveMaintenanceMessage = async () => {
    setMaintBusy(true);
    try {
      await api.post("/admin/maintenance", { enabled: maintEnabled, message: maintMsgDraft });
      await refreshMaint();
      toast.success("Message updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setMaintBusy(false);
    }
  };

  // Convert an ISO-8601 UTC string into a value the native <input type="datetime-local">
  // will render in the admin's local timezone (YYYY-MM-DDTHH:MM). Non-lossy round-trip.
  const isoToLocalInput = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const loadSchedule = async () => {
    try {
      const { data } = await api.get("/admin/maintenance");
      setSchedInfo(data);
      setSchedStart(isoToLocalInput(data.start_at));
      setSchedEnd(isoToLocalInput(data.end_at));
    } catch {
      /* non-fatal */
    }
  };

  useEffect(() => { loadSchedule(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load the current Live Leaderboard flag (default ON if the endpoint fails).
  useEffect(() => {
    api.get("/settings/leaderboard")
      .then((r) => setLeaderboardEnabled(!!r.data?.enabled))
      .catch(() => setLeaderboardEnabled(true));
  }, []);

  const toggleLeaderboard = async (next) => {
    setLeaderboardBusy(true);
    try {
      const { data } = await api.post("/admin/settings/leaderboard", { enabled: next });
      setLeaderboardEnabled(!!data.enabled);
      toast.success(next ? "Live Leaderboard is now visible on Browse." : "Live Leaderboard hidden from Browse.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed.");
    } finally {
      setLeaderboardBusy(false);
    }
  };

  const saveSchedule = async () => {
    if (!schedStart || !schedEnd) return toast.error("Set both start and end times.");
    const startMs = Date.parse(schedStart);
    const endMs = Date.parse(schedEnd);
    if (Number.isNaN(startMs) || Number.isNaN(endMs)) return toast.error("Invalid dates.");
    if (endMs <= startMs) return toast.error("End time must be after start time.");
    if (endMs <= Date.now()) return toast.error("End time is already in the past.");
    setSchedBusy(true);
    try {
      const { data } = await api.post("/admin/maintenance/schedule", {
        start_at: new Date(startMs).toISOString(),
        end_at: new Date(endMs).toISOString(),
        message: maintMsgDraft,
      });
      setSchedInfo(data);
      await refreshMaint();
      toast.success("Maintenance window scheduled — auto-flip is armed.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't schedule window.");
    } finally {
      setSchedBusy(false);
    }
  };

  const cancelSchedule = async () => {
    setSchedBusy(true);
    try {
      const { data } = await api.delete("/admin/maintenance/schedule");
      setSchedInfo(data);
      setSchedStart("");
      setSchedEnd("");
      await refreshMaint();
      toast.success("Scheduled window cancelled.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed.");
    } finally {
      setSchedBusy(false);
    }
  };

  const saveBranding = async () => {
    if (!titleDraft.trim()) return toast.error("Site title cannot be empty");
    setBrandBusy(true);
    try {
      await api.post("/admin/branding", {
        site_title: titleDraft.trim(),
        meta_description: descDraft.trim(),
        og_title: ogTitleDraft.trim(),
        og_description: ogDescDraft.trim(),
        og_image_url: ogImageDraft.trim(),
      });
      await refreshBranding();
      toast.success("Site branding updated — visitors and link previews will reflect this immediately.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBrandBusy(false);
    }
  };

  const brandingDirty = (
    titleDraft !== site_title ||
    descDraft !== meta_description ||
    ogTitleDraft !== og_title ||
    ogDescDraft !== og_description ||
    ogImageDraft !== og_image_url
  );

  // ----- Suspend / Unsuspend / Soft-remove handlers -----
  const runSoftAction = async ({ reason }) => {
    if (!softAction) return;
    setModalBusy(true);
    try {
      const { kind, id } = softAction;
      if (kind === "user.suspend") await api.post(`/admin/users/${id}/suspend`, { reason });
      else if (kind === "channel.suspend") await api.post(`/admin/channels/${id}/suspend`, { reason });
      else if (kind === "movie.remove") await api.post(`/admin/movies/${id}/remove`, { reason });
      toast.success(softAction.successMsg || "Done");
      setSoftAction(null);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setModalBusy(false);
    }
  };

  const unsuspendUser = async (id) => {
    try { await api.post(`/admin/users/${id}/unsuspend`); toast.success("User unsuspended"); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const unsuspendChannel = async (id) => {
    try { await api.post(`/admin/channels/${id}/unsuspend`); toast.success("Channel unsuspended"); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const restoreMovie = async (id) => {
    try { await api.post(`/admin/movies/${id}/restore`); toast.success("Movie restored"); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // ----- Feature toggle -----
  const toggleFeature = async (m) => {
    try {
      if (m.is_featured) {
        await api.post(`/admin/movies/${m.id}/unfeature`);
        toast.success(`"${m.title}" unfeatured.`);
      } else {
        const { data } = await api.post(`/admin/movies/${m.id}/feature`);
        if (data?.rotated_out?.title) {
          toast.success(`"${m.title}" featured. "${data.rotated_out.title}" rotated out (6-title cap).`);
        } else {
          toast.success(`"${m.title}" is now featured on the home page.`);
        }
      }
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't update feature status."); }
  };

  // ----- Featured-slots drag-to-reorder -----
  const saveFeaturedOrder = async (newOrder) => {
    setReorderBusy(true);
    try {
      const ids = newOrder.map((m) => m.id);
      await api.post("/admin/movies/featured/reorder", { ordered_ids: ids });
      toast.success("Featured order saved.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save order.");
      // On failure, re-fetch to restore truth
      try {
        const { data } = await api.get("/admin/movies/featured");
        setFeaturedOrder(data || []);
      } catch { /* ignore */ }
    } finally { setReorderBusy(false); }
  };

  const handleDragStart = (idx) => (e) => {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = "move";
    // Required for Firefox
    try { e.dataTransfer.setData("text/plain", String(idx)); } catch { /* ignore */ }
  };
  const handleDragOver = (idx) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (overIdx !== idx) setOverIdx(idx);
  };
  const handleDragLeave = () => setOverIdx(null);
  const handleDrop = (targetIdx) => (e) => {
    e.preventDefault();
    const src = dragIdx;
    setDragIdx(null);
    setOverIdx(null);
    if (src === null || src === targetIdx) return;
    const next = [...featuredOrder];
    const [moved] = next.splice(src, 1);
    next.splice(targetIdx, 0, moved);
    setFeaturedOrder(next);
    saveFeaturedOrder(next);
  };
  const handleDragEnd = () => { setDragIdx(null); setOverIdx(null); };
  const moveFeatured = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= featuredOrder.length) return;
    const next = [...featuredOrder];
    [next[idx], next[target]] = [next[target], next[idx]];
    setFeaturedOrder(next);
    saveFeaturedOrder(next);
  };

  // ----- Auto-rotate Sunday toggle + manual rotate-now -----
  const toggleAutoRotate = async (next) => {
    try {
      const { data } = await api.post("/admin/settings/featured-rotation", { enabled: !!next });
      setRotationSettings(data);
      toast.success(next ? "Auto-rotate enabled — runs every Sunday." : "Auto-rotate disabled.");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't update setting."); }
  };
  const rotateNow = async () => {
    if (featuredOrder.length < 2) {
      toast.error("Need at least 2 featured movies to rotate.");
      return;
    }
    if (!window.confirm("Shift the featured slate forward by one position now? Slot 1 will move to slot " + featuredOrder.length + ".")) return;
    setRotateBusy(true);
    try {
      await api.post("/admin/featured/rotate-now");
      toast.success("Featured slate rotated.");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't rotate.");
    } finally { setRotateBusy(false); }
  };

  // ----- Genres CRUD -----
  const createGenre = async (e) => {
    e?.preventDefault();
    const name = newGenreName.trim();
    if (!name) return;
    setGenreBusy(true);
    try {
      await api.post("/admin/genres", { name });
      toast.success(`Genre "${name}" added.`);
      setNewGenreName("");
      await refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Couldn't add genre."); }
    finally { setGenreBusy(false); }
  };
  const startEditGenre = (g) => { setEditingGenreId(g.id); setEditingGenreName(g.name); };
  const cancelEditGenre = () => { setEditingGenreId(null); setEditingGenreName(""); };
  const saveEditGenre = async () => {
    const name = editingGenreName.trim();
    if (!name) return;
    setGenreBusy(true);
    try {
      await api.patch(`/admin/genres/${editingGenreId}`, { name });
      toast.success("Genre renamed. Existing titles were updated.");
      cancelEditGenre();
      await refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Couldn't rename."); }
    finally { setGenreBusy(false); }
  };
  const deleteGenre = async (g) => {
    if (!window.confirm(`Delete genre "${g.name}"? This only works if no movie or series still uses it.`)) return;
    try {
      await api.delete(`/admin/genres/${g.id}`);
      toast.success(`"${g.name}" deleted.`);
      await refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Couldn't delete."); }
  };
  const openShelfEditor = (g) => {
    setEditingShelfId(g.id);
    setShelfTagline(g.tagline || "");
    setShelfIntro(g.intro || "");
  };
  const closeShelfEditor = () => { setEditingShelfId(null); setShelfTagline(""); setShelfIntro(""); };
  const saveShelfCopy = async () => {
    setGenreBusy(true);
    try {
      await api.patch(`/admin/genres/${editingShelfId}`, {
        tagline: shelfTagline.trim(),
        intro: shelfIntro.trim(),
      });
      toast.success("Shelf copy saved.");
      closeShelfEditor();
      await refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Couldn't save."); }
    finally { setGenreBusy(false); }
  };

  // ----- Promote / Demote (any admin) -----
  const promoteUser = async (u) => {
    if (!window.confirm(`Promote ${u.email} to admin? They'll gain full admin access immediately.`)) return;
    try {
      await api.post(`/admin/users/${u._id}/promote`);
      toast.success(`${u.email} is now an admin.`);
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't promote."); }
  };
  const demoteUser = async (u) => {
    if (!window.confirm(`Demote ${u.email} from admin to viewer? They'll lose all admin access.`)) return;
    try {
      await api.post(`/admin/users/${u._id}/demote`);
      toast.success(`${u.email} demoted to viewer.`);
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't demote."); }
  };

  // ----- Send admin invite (App Owner only) -----
  const sendInvite = async (e) => {
    e?.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviteBusy(true);
    try {
      await api.post("/admin/invites", { email: inviteEmail.trim(), message: inviteMessage.trim() });
      toast.success(`Invite sent to ${inviteEmail.trim()}.`);
      setInviteEmail(""); setInviteMessage("");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send invite.");
    } finally { setInviteBusy(false); }
  };
  const revokeInvite = async (inv) => {
    if (!window.confirm(`Revoke pending invite for ${inv.email}?`)) return;
    try {
      await api.delete(`/admin/invites/${inv.id}`);
      toast.success("Invite revoked.");
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't revoke."); }
  };

  // ----- Hard delete (typed-confirm) handlers -----
  const runHardDelete = async ({ confirm, reason }) => {
    if (!hardDelete) return;
    setModalBusy(true);
    try {
      const { kind, id } = hardDelete;
      const url = kind === "user" ? `/admin/users/${id}`
                : kind === "channel" ? `/admin/channels/${id}`
                : `/admin/movies/${id}`;
      // axios DELETE needs `data` for a body
      await api.delete(url, { data: { confirm, reason } });
      toast.success(`${kind[0].toUpperCase()}${kind.slice(1)} deleted permanently`);
      setHardDelete(null);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setModalBusy(false);
    }
  };

  const filteredUsers = users.filter((u) => {
    if (!userQ.trim()) return true;
    const q = userQ.trim().toLowerCase();
    return u.email.toLowerCase().includes(q) || (u.name || "").toLowerCase().includes(q);
  });
  const filteredChannels = channels.filter((c) => {
    if (!channelQ.trim()) return true;
    const q = channelQ.trim().toLowerCase();
    return c.name.toLowerCase().includes(q) || (c.creator_email || "").toLowerCase().includes(q);
  });

  // ---- Appeal handlers ----
  const [decidingAppeal, setDecidingAppeal] = useState(null); // { kind: "approve"|"dismiss", appeal }
  const [appealNote, setAppealNote] = useState("");
  const [appealBusy, setAppealBusy] = useState(false);

  // Cloudflare playback URL healer
  const [cfHealBusy, setCfHealBusy] = useState(false);
  const [cfHealResult, setCfHealResult] = useState(null);
  const healCfUrls = async () => {
    setCfHealBusy(true);
    try {
      const { data } = await api.post("/admin/cloudflare/heal-all");
      setCfHealResult(data);
      if (data.healed > 0) {
        toast.success(`Patched ${data.healed} movie${data.healed === 1 ? "" : "s"}.`);
      } else if (data.failed > 0) {
        toast.error(`Couldn't heal ${data.failed} — check details.`);
      } else {
        toast.success("Nothing to heal — every approved movie already has canonical playback URLs.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setCfHealBusy(false);
    }
  };

  // Per-movie Cloudflare diagnose
  const [probeMovie, setProbeMovie] = useState(null); // movie obj
  const [probeData, setProbeData] = useState(null);
  const [probeBusy, setProbeBusy] = useState(false);
  const runProbe = async (movie) => {
    setProbeMovie(movie);
    setProbeData(null);
    setProbeBusy(true);
    try {
      const { data } = await api.get(`/movies/${movie.id}/cf-probe`);
      setProbeData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Probe failed");
      setProbeMovie(null);
    } finally {
      setProbeBusy(false);
    }
  };

  const decideAppeal = async () => {
    if (!decidingAppeal) return;
    setAppealBusy(true);
    try {
      const url = decidingAppeal.kind === "approve"
        ? `/admin/appeals/${decidingAppeal.appeal.id}/approve`
        : `/admin/appeals/${decidingAppeal.appeal.id}/dismiss`;
      await api.post(url, { admin_note: appealNote.trim() });
      toast.success(decidingAppeal.kind === "approve" ? "Appeal approved — movie restored." : "Appeal dismissed.");
      setDecidingAppeal(null);
      setAppealNote("");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setAppealBusy(false);
    }
  };

  const movieList = tab === "pending" ? pending : all;

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-12" data-testid="admin-panel">
      <div className="flex items-center gap-3 mb-2">
        <ShieldCheck className="h-6 w-6 text-[#E65F2B]" />
        <p className="text-eyebrow">Moderation Console</p>
      </div>
      <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">Admin Panel</h1>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mt-8">
          {[
            { label: "Users", value: stats.users, icon: Users, sub: stats.suspended_users ? `${stats.suspended_users} suspended` : null },
            { label: "Creators", value: stats.creators, icon: BadgeCheck, sub: stats.suspended_channels ? `${stats.suspended_channels} ch. suspended` : null },
            { label: "Active subs", value: stats.active_subs, icon: CheckCircle2 },
            { label: "Movies", value: stats.movies, icon: Film, sub: stats.removed ? `${stats.removed} removed` : null },
            { label: "Pending", value: stats.pending, icon: Clock },
            { label: "Approved", value: stats.approved, icon: CheckCircle2 },
          ].map((s) => (
            <div key={s.label} className="card-surface p-4">
              <s.icon className="h-4 w-4 text-white/40 mb-2" />
              <p className="font-display text-2xl font-black">{s.value}</p>
              <p className="text-xs text-white/50">{s.label}</p>
              {s.sub && <p className="text-[10px] text-yellow-400 mt-1">{s.sub}</p>}
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2 mt-10 border-b border-white/5 overflow-x-auto row-scroll">
        <button onClick={() => setTab("pending")} data-testid="admin-tab-pending" className={`px-4 py-3 text-sm font-medium border-b-2 transition whitespace-nowrap ${tab === "pending" ? "border-[#E65F2B] text-white" : "border-transparent text-white/40 hover:text-white"}`}>
          Pending ({pending.length})
        </button>
        <button onClick={() => setTab("all")} data-testid="admin-tab-all" className={`px-4 py-3 text-sm font-medium border-b-2 transition whitespace-nowrap ${tab === "all" ? "border-[#E65F2B] text-white" : "border-transparent text-white/40 hover:text-white"}`}>
          All movies ({all.length})
        </button>
        <button onClick={() => setTab("users")} data-testid="admin-tab-users" className={`px-4 py-3 text-sm font-medium border-b-2 transition whitespace-nowrap ${tab === "users" ? "border-[#E65F2B] text-white" : "border-transparent text-white/40 hover:text-white"}`}>
          Users ({users.length}){stats?.suspended_users ? <span className="ml-1 text-yellow-400">·{stats.suspended_users}</span> : null}
        </button>
        <button onClick={() => setTab("channels")} data-testid="admin-tab-channels" className={`px-4 py-3 text-sm font-medium border-b-2 transition whitespace-nowrap ${tab === "channels" ? "border-[#E65F2B] text-white" : "border-transparent text-white/40 hover:text-white"}`}>
          Channels ({channels.length}){stats?.suspended_channels ? <span className="ml-1 text-yellow-400">·{stats.suspended_channels}</span> : null}
        </button>
        <button onClick={() => setTab("payouts")} data-testid="admin-tab-payouts" className={`px-4 py-3 text-sm font-medium border-b-2 transition whitespace-nowrap ${tab === "payouts" ? "border-[#E65F2B] text-white" : "border-transparent text-white/40 hover:text-white"}`}>
          Earnings{payouts.length ? <span className="ml-1 text-[#E65F2B]">·{payouts.length}</span> : null}
        </button>
        <button onClick={() => setTab("appeals")} data-testid="admin-tab-appeals" className={`px-4 py-3 text-sm font-medium border-b-2 transition whitespace-nowrap ${tab === "appeals" ? "border-[#E65F2B] text-white" : "border-transparent text-white/40 hover:text-white"}`}>
          Appeals ({appeals.filter(a => a.status === "pending").length} pending)
        </button>
        <button onClick={() => setTab("audit")} data-testid="admin-tab-audit" className={`px-4 py-3 text-sm font-medium border-b-2 transition whitespace-nowrap ${tab === "audit" ? "border-[#E65F2B] text-white" : "border-transparent text-white/40 hover:text-white"}`}>
          Audit log
        </button>
        <button onClick={() => setTab("genres")} data-testid="admin-tab-genres" className={`px-4 py-3 text-sm font-medium border-b-2 transition whitespace-nowrap ${tab === "genres" ? "border-[#E65F2B] text-white" : "border-transparent text-white/40 hover:text-white"}`}>
          Genres
        </button>
        <button onClick={() => setTab("newsletter")} data-testid="admin-tab-newsletter" className={`px-4 py-3 text-sm font-medium border-b-2 transition whitespace-nowrap ${tab === "newsletter" ? "border-[#E65F2B] text-white" : "border-transparent text-white/40 hover:text-white"}`}>
          Newsletter
        </button>
        {isAppOwner && (
          <button onClick={() => setTab("invites")} data-testid="admin-tab-invites" className={`px-4 py-3 text-sm font-medium border-b-2 transition whitespace-nowrap ${tab === "invites" ? "border-[#E65F2B] text-white" : "border-transparent text-white/40 hover:text-white"}`}>
            Invites{invites.length ? <span className="ml-1 text-[#E65F2B]">·{invites.length}</span> : null}
          </button>
        )}
        <button onClick={() => setTab("system")} data-testid="admin-tab-system" className={`px-4 py-3 text-sm font-medium border-b-2 transition whitespace-nowrap ${tab === "system" ? "border-[#E65F2B] text-white" : "border-transparent text-white/40 hover:text-white"}`}>
          System {maintEnabled && <span className="ml-1 inline-block h-2 w-2 rounded-full bg-[#E65F2B] animate-pulse" />}
        </button>
      </div>

      <div className="mt-8 space-y-3">
        {tab === "system" && (
          <>
            <div className="card-surface p-8 max-w-2xl" data-testid="admin-branding-panel">
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 rounded-xl flex items-center justify-center shrink-0 bg-[#EAB308]/15 border border-[#EAB308]/30">
                  <Type className="h-6 w-6 text-[#EAB308]" />
                </div>
                <div className="flex-1">
                  <h3 className="font-display text-2xl font-bold">Site Branding</h3>
                  <p className="text-sm text-white/50 mt-1">
                    Controls the browser tab title and SEO meta description. Updates apply instantly to every visitor.
                  </p>
                </div>
              </div>
              <div className="mt-6 space-y-4">
                <div>
                  <label className="text-eyebrow mb-2 block">Browser tab title</label>
                  <input
                    value={titleDraft}
                    onChange={(e) => setTitleDraft(e.target.value)}
                    maxLength={120}
                    className="input-dark"
                    placeholder="e.g. Christoreel | African Christian Movies"
                    data-testid="branding-title-input"
                  />
                  <p className="text-xs text-white/30 mt-1">{titleDraft.length}/120</p>
                </div>
                <div>
                  <label className="text-eyebrow mb-2 block">Meta description (SEO)</label>
                  <textarea
                    value={descDraft}
                    onChange={(e) => setDescDraft(e.target.value)}
                    maxLength={300}
                    rows={3}
                    className="input-dark resize-none"
                    placeholder="A short tagline shown in Google search results..."
                    data-testid="branding-desc-input"
                  />
                  <p className="text-xs text-white/30 mt-1">{descDraft.length}/300</p>
                </div>

                <div className="pt-4 mt-2 border-t border-white/5">
                  <p className="text-eyebrow text-[#EAB308] mb-1">Social link previews (Open Graph)</p>
                  <p className="text-xs text-white/40 mb-4">
                    Controls the title, blurb, and image card shown when Christoreel links are pasted into Facebook, X/Twitter, WhatsApp, LinkedIn, iMessage, Slack, etc.
                  </p>
                </div>

                <div>
                  <label className="text-eyebrow mb-2 block">Share preview title</label>
                  <input
                    value={ogTitleDraft}
                    onChange={(e) => setOgTitleDraft(e.target.value)}
                    maxLength={120}
                    className="input-dark"
                    placeholder="Defaults to the site title above"
                    data-testid="branding-og-title-input"
                  />
                  <p className="text-xs text-white/30 mt-1">{ogTitleDraft.length}/120</p>
                </div>
                <div>
                  <label className="text-eyebrow mb-2 block">Share preview description</label>
                  <textarea
                    value={ogDescDraft}
                    onChange={(e) => setOgDescDraft(e.target.value)}
                    maxLength={300}
                    rows={2}
                    className="input-dark resize-none"
                    placeholder="e.g. Stream curated African Christian movies. New titles weekly. From ₦2,000/mo."
                    data-testid="branding-og-desc-input"
                  />
                  <p className="text-xs text-white/30 mt-1">{ogDescDraft.length}/300</p>
                </div>
                <div>
                  <label className="text-eyebrow mb-2 block">Share preview image URL (1200×630 recommended)</label>
                  <input
                    value={ogImageDraft}
                    onChange={(e) => setOgImageDraft(e.target.value)}
                    maxLength={500}
                    className="input-dark"
                    placeholder="https://christoreel.com/og-default.jpg"
                    data-testid="branding-og-image-input"
                  />
                  <p className="text-xs text-white/30 mt-1">{ogImageDraft.length}/500 — must be a publicly accessible https URL</p>
                  {ogImageDraft && (
                    <div className="mt-3">
                      <p className="text-xs text-white/40 mb-2">Preview:</p>
                      <img
                        src={ogImageDraft}
                        alt="OG preview"
                        className="max-h-32 rounded-lg border border-white/10 object-cover"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                        data-testid="branding-og-image-preview"
                      />
                    </div>
                  )}
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={saveBranding}
                    disabled={brandBusy || !brandingDirty}
                    className="ember-btn disabled:opacity-40"
                    data-testid="branding-save-button"
                  >
                    {brandBusy ? "Saving..." : "Save branding"}
                  </button>
                </div>
              </div>
            </div>

            <div className="card-surface p-8 max-w-2xl" data-testid="admin-system-panel">
            <div className="flex items-start gap-4">
              <div className={`h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ${maintEnabled ? "bg-[#E65F2B]/15 border border-[#E65F2B]/30" : "bg-white/5 border border-white/10"}`}>
                <Wrench className={`h-6 w-6 ${maintEnabled ? "text-[#E65F2B]" : "text-white/40"}`} />
              </div>
              <div className="flex-1">
                <h3 className="font-display text-2xl font-bold">Maintenance Mode</h3>
                <p className="text-sm text-white/50 mt-1">
                  When enabled, public visitors and signed-in users see a &ldquo;We&apos;ll be back&rdquo; screen, and most APIs return 503. Admins remain able to sign in and toggle this off.
                </p>
                <div className="mt-4 flex items-center gap-3 flex-wrap">
                  <span
                    className={`badge-pill text-[10px] ${maintEnabled ? "bg-[#E65F2B]/15 text-[#E65F2B] border border-[#E65F2B]/30" : "bg-green-500/10 text-green-400 border border-green-500/30"}`}
                    data-testid="maintenance-status"
                  >
                    {maintEnabled ? "● ENABLED" : "● DISABLED"}
                  </span>
                  <button
                    onClick={() => toggleMaintenance(!maintEnabled)}
                    disabled={maintBusy}
                    data-testid={maintEnabled ? "maintenance-disable-button" : "maintenance-enable-button"}
                    className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold transition disabled:opacity-50 ${
                      maintEnabled
                        ? "bg-green-600 hover:bg-green-500 text-white"
                        : "bg-[#E65F2B] hover:bg-[#F97316] text-white"
                    }`}
                  >
                    <Power className="h-4 w-4" />
                    {maintBusy ? "Saving..." : maintEnabled ? "Disable maintenance" : "Enable maintenance"}
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-white/5">
              <label className="text-eyebrow mb-2 block">Public-facing message</label>
              <textarea
                value={maintMsgDraft}
                onChange={(e) => setMaintMsgDraft(e.target.value)}
                rows={4}
                maxLength={400}
                className="input-dark resize-none"
                placeholder="What viewers will see while the site is down..."
                data-testid="maintenance-message-input"
              />
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-white/30">{maintMsgDraft.length}/400</span>
                <button
                  onClick={saveMaintenanceMessage}
                  disabled={maintBusy || maintMsgDraft === maintMessage}
                  className="ghost-btn !py-2 !px-4 text-sm disabled:opacity-40"
                  data-testid="maintenance-save-message-button"
                >
                  Save message
                </button>
              </div>
            </div>

            {/* Scheduled window — auto-enable at start, auto-disable at end.
                A 24h heads-up email is sent to creators + active viewers. */}
            <div className="mt-8 pt-6 border-t border-white/5" data-testid="maintenance-schedule-section">
              <div className="flex items-baseline justify-between mb-1">
                <label className="text-eyebrow">Schedule a maintenance window</label>
                {schedInfo?.notified_at && (
                  <span className="text-[10px] text-[#EAB308]" data-testid="schedule-notified-flag">
                    24h heads-up sent
                  </span>
                )}
              </div>
              <p className="text-xs text-white/40 mb-3">
                Auto-enables at Start and auto-disables at End. Creators + active viewers get a heads-up email 24 hours before Start.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] text-white/40 uppercase tracking-wider mb-1 block">Start (your local time)</label>
                  <input
                    type="datetime-local"
                    value={schedStart}
                    onChange={(e) => setSchedStart(e.target.value)}
                    className="input-dark"
                    disabled={schedBusy}
                    data-testid="schedule-start-input"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-white/40 uppercase tracking-wider mb-1 block">End</label>
                  <input
                    type="datetime-local"
                    value={schedEnd}
                    onChange={(e) => setSchedEnd(e.target.value)}
                    className="input-dark"
                    disabled={schedBusy}
                    data-testid="schedule-end-input"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <button
                  onClick={saveSchedule}
                  disabled={schedBusy || !schedStart || !schedEnd}
                  className="ember-btn !py-2 !px-4 text-sm disabled:opacity-40"
                  data-testid="schedule-save-button"
                >
                  {schedBusy ? "Saving..." : (schedInfo?.start_at ? "Update schedule" : "Schedule window")}
                </button>
                {schedInfo?.start_at && (
                  <button
                    onClick={cancelSchedule}
                    disabled={schedBusy}
                    className="ghost-btn !py-2 !px-4 text-sm disabled:opacity-40"
                    data-testid="schedule-cancel-button"
                  >
                    Cancel schedule
                  </button>
                )}
              </div>
              {schedInfo?.start_at && schedInfo?.end_at && (
                <p className="text-xs text-white/50 mt-3" data-testid="schedule-summary">
                  Scheduled by <b className="text-white/70">{schedInfo.scheduled_by || "admin"}</b> ·{" "}
                  {new Date(schedInfo.start_at).toLocaleString()} → {new Date(schedInfo.end_at).toLocaleString()}
                </p>
              )}
            </div>
          </div>

          <div className="card-surface p-8 max-w-2xl mt-6" data-testid="admin-leaderboard-panel">
            <div className="flex items-start gap-4">
              <div className={`h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ${
                leaderboardEnabled ? "bg-[#EAB308]/15 border border-[#EAB308]/30" : "bg-white/5 border border-white/10"
              }`}>
                <Trophy className={`h-6 w-6 ${leaderboardEnabled ? "text-[#EAB308]" : "text-white/40"}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-display text-2xl font-bold">Live Leaderboard</h3>
                  <span
                    className={`badge-pill text-[10px] ${
                      leaderboardEnabled
                        ? "bg-green-500/15 text-green-400 border border-green-500/30"
                        : "bg-white/5 text-white/40 border border-white/10"
                    }`}
                    data-testid="leaderboard-status-badge"
                  >
                    {leaderboardEnabled ? "VISIBLE" : "HIDDEN"}
                  </span>
                </div>
                <p className="text-sm text-white/50 mt-1">
                  The <b className="text-white/80">Top-earning Films this month</b> rail on the public <code className="px-1 py-0.5 bg-white/5 rounded font-mono text-[11px]">/browse</code> page. Toggle off to hide it (e.g., during off-cycle months or if the ranking looks uneven).
                </p>
              </div>
            </div>
            <div className="mt-5">
              <button
                onClick={() => toggleLeaderboard(!leaderboardEnabled)}
                disabled={leaderboardBusy}
                className={leaderboardEnabled ? "ghost-btn disabled:opacity-40" : "ember-btn disabled:opacity-40"}
                data-testid={leaderboardEnabled ? "leaderboard-disable-button" : "leaderboard-enable-button"}
              >
                <Trophy className="h-4 w-4" />
                {leaderboardBusy
                  ? "Saving..."
                  : leaderboardEnabled
                  ? "Hide leaderboard on Browse"
                  : "Show leaderboard on Browse"}
              </button>
            </div>
          </div>

          <div className="card-surface p-8 max-w-2xl mt-6" data-testid="admin-cf-heal-panel">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-xl flex items-center justify-center shrink-0 bg-[#E65F2B]/15 border border-[#E65F2B]/30">
                <RefreshCw className="h-6 w-6 text-[#E65F2B]" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-display text-2xl font-bold">Cloudflare playback URLs</h3>
                <p className="text-sm text-white/50 mt-1">
                  Re-fetches the canonical HLS / thumbnail URLs from Cloudflare for every approved movie missing them. Use this after a Cloudflare customer-code change or if viewers report a recurring &ldquo;playback error&rdquo;.
                </p>
                <p className="text-xs text-white/30 mt-2">
                  Safe to run multiple times — only movies missing <code className="px-1 py-0.5 bg-white/5 rounded font-mono text-[11px]">playback_hls_url</code> are touched.
                </p>
              </div>
            </div>
            <div className="mt-5 flex items-center gap-3 flex-wrap">
              <button
                onClick={healCfUrls}
                disabled={cfHealBusy}
                className="ember-btn disabled:opacity-40"
                data-testid="heal-cf-urls-button"
              >
                <RefreshCw className={`h-4 w-4 ${cfHealBusy ? "animate-spin" : ""}`} />
                {cfHealBusy ? "Healing..." : "Heal CF URLs"}
              </button>
              {cfHealResult && (
                <span className="text-xs" data-testid="cf-heal-result">
                  <span className={`badge-pill ${cfHealResult.healed > 0 ? "bg-green-500/15 text-green-400 border border-green-500/30" : "bg-white/5 text-white/40 border border-white/10"}`}>
                    {cfHealResult.healed} healed
                  </span>
                  {cfHealResult.failed > 0 && (
                    <span className="badge-pill bg-red-500/15 text-red-400 border border-red-500/30 ml-2">
                      {cfHealResult.failed} failed
                    </span>
                  )}
                </span>
              )}
            </div>
            {cfHealResult?.details?.length > 0 && (
              <details className="mt-4">
                <summary className="text-xs text-white/40 cursor-pointer hover:text-white">Show per-movie report</summary>
                <ul className="mt-2 space-y-1 text-xs max-h-60 overflow-y-auto" data-testid="cf-heal-details">
                  {cfHealResult.details.map((d) => (
                    <li key={d.id} className={`px-3 py-1.5 rounded ${d.ok ? "bg-green-500/5 text-green-300" : "bg-red-500/5 text-red-300"}`}>
                      {d.ok ? "✓" : "✗"} <span className="font-medium">{d.title}</span>
                      {d.reason && <span className="text-white/40"> — {d.reason}</span>}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
          </>
        )}

        {tab === "payouts" && <AdminEarnings />}

        {/* Featured slots reorder panel (only on "all" tab when there are featured movies) */}
        {tab === "all" && featuredOrder.length > 0 && (
          <div className="card-surface p-5 sm:p-6 mb-6 border-[#E65F2B]/20" data-testid="featured-slots-panel">
            <div className="flex items-start gap-3 mb-4 flex-wrap">
              <div className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0 bg-[#E65F2B]/15 border border-[#E65F2B]/30">
                <Sparkles className="h-4 w-4 text-[#E65F2B]" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-display text-lg font-bold">Featured slots · drag to reorder</h3>
                <p className="text-xs text-white/50 mt-0.5">
                  {featuredOrder.length} of 6 slots filled. Top-left appears first on the home page. Changes save automatically.
                </p>
              </div>
              {reorderBusy && <span className="text-xs text-[#E65F2B] self-center">Saving...</span>}
            </div>

            {/* Auto-rotate weekly controls */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5 p-3 rounded-lg bg-white/[0.02] border border-white/5" data-testid="auto-rotate-row">
              <div className="flex items-start gap-3 min-w-0">
                <RefreshCw className={`h-4 w-4 mt-0.5 shrink-0 ${rotationSettings.enabled ? "text-[#E65F2B]" : "text-white/30"}`} />
                <div className="min-w-0">
                  <label htmlFor="auto-rotate-switch" className="text-sm font-medium cursor-pointer">
                    Auto-rotate every Sunday
                  </label>
                  <p className="text-xs text-white/40 mt-0.5">
                    {rotationSettings.enabled
                      ? "Shifts slot 1 to the end every Sunday so the home page feels fresh."
                      : "Disabled. Featured order stays exactly as you set it."}
                    {rotationSettings.last_rotated_at && (
                      <> · last rotated {new Date(rotationSettings.last_rotated_at).toLocaleString()}</>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={rotateNow}
                  disabled={rotateBusy || featuredOrder.length < 2}
                  className="ghost-btn !py-2 !px-3 text-xs disabled:opacity-40"
                  data-testid="rotate-now-button"
                  title="Manually rotate the slate by one position now"
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${rotateBusy ? "animate-spin" : ""}`} />
                  Rotate now
                </button>
                <Switch
                  id="auto-rotate-switch"
                  checked={!!rotationSettings.enabled}
                  onCheckedChange={toggleAutoRotate}
                  data-testid="auto-rotate-switch"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="featured-slots-grid">
              {featuredOrder.map((m, idx) => (
                <div
                  key={m.id}
                  draggable
                  onDragStart={handleDragStart(idx)}
                  onDragOver={handleDragOver(idx)}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop(idx)}
                  onDragEnd={handleDragEnd}
                  className={`group relative aspect-[2/3] rounded-lg overflow-hidden border bg-white/[0.03] transition-all cursor-grab active:cursor-grabbing
                    ${dragIdx === idx ? "opacity-40 scale-95" : ""}
                    ${overIdx === idx && dragIdx !== idx ? "border-[#E65F2B] ring-2 ring-[#E65F2B]/40 scale-[1.02]" : "border-white/10 hover:border-white/30"}`}
                  data-testid={`featured-slot-${idx}`}
                  data-movie-id={m.id}
                >
                  {(m.cloudflare_thumbnail_url || m.poster_path) ? (
                    <img src={m.cloudflare_thumbnail_url || m.poster_path} alt={m.title} className="absolute inset-0 h-full w-full object-cover pointer-events-none" />
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] flex items-center justify-center">
                      <Film className="h-7 w-7 text-white/20" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent pointer-events-none" />
                  {/* Position indicator */}
                  <div className="absolute top-2 left-2 inline-flex items-center justify-center h-6 w-6 rounded-md bg-[#E65F2B] text-white text-xs font-bold pointer-events-none">
                    {idx + 1}
                  </div>
                  {/* Drag handle */}
                  <div className="absolute top-2 right-2 inline-flex items-center justify-center h-6 w-6 rounded-md bg-black/60 backdrop-blur-sm text-white/70 pointer-events-none">
                    <GripVertical className="h-3.5 w-3.5" />
                  </div>
                  {/* Caption */}
                  <div className="absolute bottom-0 left-0 right-0 p-3 pointer-events-none">
                    <p className="font-display font-bold text-sm text-white truncate">{m.title}</p>
                    <p className="text-[10px] text-white/60 truncate">{m.channel_name}</p>
                  </div>
                  {/* Mobile arrow controls (visible on small screens) */}
                  <div className="absolute inset-y-0 right-0 flex flex-col justify-center gap-1 p-1 sm:opacity-0 sm:group-hover:opacity-100 transition">
                    <button
                      onClick={() => moveFeatured(idx, -1)}
                      disabled={idx === 0 || reorderBusy}
                      className="h-6 w-6 rounded-md bg-black/70 backdrop-blur-sm text-white/80 hover:text-white hover:bg-[#E65F2B] disabled:opacity-30 disabled:hover:bg-black/70 flex items-center justify-center"
                      data-testid={`featured-slot-up-${idx}`}
                      title="Move left/up"
                    >
                      <ChevronUp className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => moveFeatured(idx, 1)}
                      disabled={idx === featuredOrder.length - 1 || reorderBusy}
                      className="h-6 w-6 rounded-md bg-black/70 backdrop-blur-sm text-white/80 hover:text-white hover:bg-[#E65F2B] disabled:opacity-30 disabled:hover:bg-black/70 flex items-center justify-center"
                      data-testid={`featured-slot-down-${idx}`}
                      title="Move right/down"
                    >
                      <ChevronDown className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
              {/* Empty slot placeholders */}
              {Array.from({ length: Math.max(0, 6 - featuredOrder.length) }).map((_, i) => (
                <div
                  key={`empty-${i}`}
                  className="aspect-[2/3] rounded-lg border border-dashed border-white/10 bg-white/[0.01] flex items-center justify-center"
                  data-testid={`featured-slot-empty-${i}`}
                >
                  <span className="text-[10px] uppercase tracking-widest text-white/30">Empty slot</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {(tab === "pending" || tab === "all") && movieList.map((m) => (
          <div key={m.id} className="card-surface p-5 flex flex-col sm:flex-row gap-4" data-testid={`admin-movie-${m.id}`}>
            <div className="w-full sm:w-28 h-40 sm:h-40 rounded-md bg-black overflow-hidden shrink-0">
              <img src={posterUrl(m.id)} alt="" className="h-full w-full object-cover" onError={(e) => e.currentTarget.style.display = "none"} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-display font-bold text-lg">{m.title}</h3>
                <span className={`badge-pill text-[10px] ${m.status === "approved" ? "bg-green-500/10 text-green-400 border border-green-500/30" : m.status === "pending" ? "bg-yellow-500/10 text-yellow-400 border border-yellow-500/30" : m.status === "removed" ? "bg-red-500/15 text-red-300 border border-red-500/40" : "bg-red-500/10 text-red-400 border border-red-500/30"}`}>
                  {m.status}
                </span>
                {m.is_featured && (
                  <span className="badge-pill text-[10px] bg-[#E65F2B]/15 text-[#E65F2B] border border-[#E65F2B]/40" data-testid={`featured-badge-${m.id}`}>
                    <Sparkles className="h-3 w-3 mr-1" /> Featured
                  </span>
                )}
              </div>
              <p className="text-sm text-white/50 mt-1">Channel: {m.channel_name} · {m.genre}</p>
              <p className="text-sm text-white/70 mt-3 line-clamp-3">{m.description || "No description"}</p>
              {m.rejection_reason && <p className="text-xs text-red-400 mt-2">Rejected: {m.rejection_reason}</p>}

              {rejecting === m.id ? (
                <div className="mt-4 flex gap-2">
                  <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Rejection reason..." className="input-dark" data-testid={`reject-reason-input-${m.id}`} />
                  <button onClick={() => moderate(m.id, "rejected", reason)} className="ember-btn whitespace-nowrap" data-testid={`confirm-reject-${m.id}`}>Confirm</button>
                  <button onClick={() => setRejecting(null)} className="ghost-btn whitespace-nowrap">Cancel</button>
                </div>
              ) : (
                <div className="flex gap-2 mt-4 flex-wrap">
                  <Link to={`/movie/${m.id}`} className="ghost-btn !py-2 !px-4 text-sm" data-testid={`admin-preview-${m.id}`}>Preview</Link>
                  {m.cloudflare_uid && (
                    <button
                      onClick={() => runProbe(m)}
                      className="ghost-btn !py-2 !px-4 text-sm hover:border-[#E65F2B]/50 hover:text-[#E65F2B]"
                      data-testid={`probe-${m.id}`}
                      title="Inspect this movie's status on Cloudflare"
                    >
                      <Stethoscope className="h-4 w-4" /> Diagnose
                    </button>
                  )}
                  {m.status !== "approved" && m.status !== "removed" && (
                    <button onClick={() => moderate(m.id, "approved")} className="ember-btn !py-2 !px-4 text-sm bg-green-600 hover:bg-green-500" data-testid={`approve-${m.id}`}>
                      <CheckCircle2 className="h-4 w-4" /> Approve
                    </button>
                  )}
                  {m.status === "approved" && (
                    <button
                      onClick={() => toggleFeature(m)}
                      className={`ghost-btn !py-2 !px-4 text-sm ${m.is_featured ? "border-[#E65F2B]/50 text-[#E65F2B] hover:border-[#E65F2B]" : "hover:border-[#E65F2B]/50 hover:text-[#E65F2B]"}`}
                      data-testid={`feature-toggle-${m.id}`}
                      title={m.is_featured ? "Remove from the home page feature trio" : "Pin to the home page feature trio (max 6)"}
                    >
                      <Sparkles className="h-4 w-4" /> {m.is_featured ? "Unfeature" : "Feature"}
                    </button>
                  )}
                  {m.status !== "rejected" && m.status !== "removed" && (
                    <button onClick={() => setRejecting(m.id)} className="ghost-btn !py-2 !px-4 text-sm hover:border-red-500/50 hover:text-red-400" data-testid={`reject-${m.id}`}>
                      <XCircle className="h-4 w-4" /> Reject
                    </button>
                  )}
                  {m.status === "removed" ? (
                    <button onClick={() => restoreMovie(m.id)} className="ghost-btn !py-2 !px-4 text-sm hover:border-green-500/50 hover:text-green-400" data-testid={`restore-${m.id}`}>
                      <RotateCcw className="h-4 w-4" /> Restore
                    </button>
                  ) : (
                    <button
                      onClick={() => setSoftAction({
                        kind: "movie.remove", id: m.id,
                        title: `Remove "${m.title}"?`,
                        description: <>
                          <p>Soft-deletes this movie. It disappears from the public catalog immediately, but the file stays on Cloudflare for ~30 days so it can be restored if appealed. The creator gets an email notification.</p>
                        </>,
                        confirmButtonLabel: "Remove from catalog",
                        successMsg: "Movie removed from the public catalog.",
                      })}
                      className="ghost-btn !py-2 !px-4 text-sm hover:border-yellow-500/50 hover:text-yellow-400"
                      data-testid={`remove-${m.id}`}
                    >
                      <ShieldOff className="h-4 w-4" /> Remove
                    </button>
                  )}
                  <button
                    onClick={() => setHardDelete({
                      kind: "movie", id: m.id, confirmText: m.title, label: "movie title",
                      description: <>
                        <p><b>Permanently deletes</b> this movie from MongoDB and releases the Cloudflare Stream slot. This <b>cannot be undone</b>.</p>
                        <p className="text-yellow-400">Prefer <b>Remove</b> for moderation takedowns — it&apos;s reversible. Use Delete only for spam, abuse, or copyright takedowns where the file must not be recoverable.</p>
                      </>,
                    })}
                    className="ghost-btn !py-2 !px-4 text-sm hover:border-red-500/50 hover:text-red-400"
                    data-testid={`hard-delete-movie-${m.id}`}
                  >
                    <Trash2 className="h-4 w-4" /> Delete
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}

        {(tab === "pending" || tab === "all") && movieList.length === 0 && (
          <div className="card-surface p-12 text-center text-white/50" data-testid="admin-empty">
            Nothing here yet.
          </div>
        )}

        {tab === "users" && (
          <>
            <div className="flex items-center gap-2 mb-2">
              <Search className="h-4 w-4 text-white/40" />
              <input
                value={userQ}
                onChange={(e) => setUserQ(e.target.value)}
                placeholder="Filter by name or email…"
                className="input-dark"
                data-testid="admin-users-search"
              />
            </div>
            {filteredUsers.length === 0 ? (
              <div className="card-surface p-12 text-center text-white/50" data-testid="admin-users-empty">
                No users match.
              </div>
            ) : filteredUsers.map((u) => {
              const isSelf = me && (me._id === u._id);
              const isAdminRow = u.role === "admin";
              const suspended = u.is_active === false;
              return (
                <div key={u._id} className="card-surface p-5" data-testid={`admin-user-${u._id}`}>
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-display font-bold text-base break-all">{u.name || "—"}</p>
                        <span className="text-xs text-white/40 break-all">{u.email}</span>
                        <span className={`badge-pill text-[10px] ${u.role === "admin" ? "bg-[#EAB308]/15 text-[#EAB308] border border-[#EAB308]/30" : u.role === "creator" ? "bg-[#E65F2B]/15 text-[#E65F2B] border border-[#E65F2B]/30" : "bg-white/5 text-white/60 border border-white/10"}`}>
                          {u.role}
                        </span>
                        {suspended && (
                          <span className="badge-pill text-[10px] bg-yellow-500/15 text-yellow-400 border border-yellow-500/30">
                            suspended
                          </span>
                        )}
                        {u.subscription_status === "active" && (
                          <span className="badge-pill text-[10px] bg-green-500/15 text-green-400 border border-green-500/30">
                            sub: active
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-white/40 mt-2">
                        Joined {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                        {u.channel ? <> · Channel: <Link to={`/channel/${u.channel.id}`} className="text-white/70 underline-offset-2 hover:underline">{u.channel.name}</Link></> : null}
                        {typeof u.movies_count === "number" ? <> · {u.movies_count} movie{u.movies_count === 1 ? "" : "s"}</> : null}
                      </p>
                      {u.suspend_reason && <p className="text-xs text-yellow-300 mt-1">Suspended: {u.suspend_reason}</p>}
                    </div>
                    <div className="flex gap-2 flex-wrap shrink-0">
                      {!isAdminRow && !isSelf && !suspended && (
                        <button
                          onClick={() => promoteUser(u)}
                          className="ghost-btn !py-2 !px-4 text-sm hover:border-[#EAB308]/50 hover:text-[#EAB308]"
                          data-testid={`promote-user-${u._id}`}
                          title="Promote to admin"
                        >
                          <ArrowUpCircle className="h-4 w-4" /> Promote
                        </button>
                      )}
                      {isAdminRow && !isSelf && !u.is_app_owner && (
                        <button
                          onClick={() => demoteUser(u)}
                          className="ghost-btn !py-2 !px-4 text-sm hover:border-orange-500/50 hover:text-orange-400"
                          data-testid={`demote-user-${u._id}`}
                          title="Demote to viewer"
                        >
                          <ArrowDownCircle className="h-4 w-4" /> Demote
                        </button>
                      )}
                      {!isAdminRow && !isSelf && (suspended ? (
                        <button
                          onClick={() => unsuspendUser(u._id)}
                          className="ghost-btn !py-2 !px-4 text-sm hover:border-green-500/50 hover:text-green-400"
                          data-testid={`unsuspend-user-${u._id}`}
                        >
                          <ShieldCheck className="h-4 w-4" /> Unsuspend
                        </button>
                      ) : (
                        <button
                          onClick={() => setSoftAction({
                            kind: "user.suspend", id: u._id,
                            title: `Suspend ${u.email}?`,
                            description: <>
                              <p>The user will be signed out and blocked from logging in. Their channel + movies are hidden from the public catalog. Their subscription billing continues unless you cancel it separately.</p>
                              <p className="text-white/40">This is reversible — you can Unsuspend any time.</p>
                            </>,
                            confirmButtonLabel: "Suspend user",
                            successMsg: "User suspended",
                          })}
                          className="ghost-btn !py-2 !px-4 text-sm hover:border-yellow-500/50 hover:text-yellow-400"
                          data-testid={`suspend-user-${u._id}`}
                        >
                          <ShieldOff className="h-4 w-4" /> Suspend
                        </button>
                      ))}
                      {!isAdminRow && !isSelf && (
                        <button
                          onClick={() => setHardDelete({
                            kind: "user", id: u._id, confirmText: u.email, label: "user's email",
                            description: <>
                              <p><b>Permanently deletes</b> {u.email} and cascades:</p>
                              <ul className="list-disc ml-5 text-white/60">
                                <li>Their channel{u.channel ? <> &quot;{u.channel.name}&quot;</> : ""} and all {u.movies_count || 0} movies</li>
                                <li>All Cloudflare Stream slots and transactions</li>
                                <li>Active Paystack subscription (disabled)</li>
                              </ul>
                              <p className="text-yellow-400">This <b>cannot be undone</b>. Prefer Suspend for reversible moderation.</p>
                            </>,
                          })}
                          className="ghost-btn !py-2 !px-4 text-sm hover:border-red-500/50 hover:text-red-400"
                          data-testid={`hard-delete-user-${u._id}`}
                        >
                          <Trash2 className="h-4 w-4" /> Delete
                        </button>
                      )}
                      {(isSelf || u.is_app_owner) && (
                        <span className="text-[10px] text-white/30 px-3 py-2" data-testid={`admin-user-protected-${u._id}`}>
                          {isSelf ? "(this is you)" : "(app owner)"}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        )}

        {tab === "channels" && (
          <>
            <div className="flex items-center gap-2 mb-2">
              <Search className="h-4 w-4 text-white/40" />
              <input
                value={channelQ}
                onChange={(e) => setChannelQ(e.target.value)}
                placeholder="Filter by channel or creator email…"
                className="input-dark"
                data-testid="admin-channels-search"
              />
            </div>
            {filteredChannels.length === 0 ? (
              <div className="card-surface p-12 text-center text-white/50" data-testid="admin-channels-empty">
                No channels match.
              </div>
            ) : filteredChannels.map((c) => (
              <div key={c.id} className="card-surface p-5" data-testid={`admin-channel-${c.id}`}>
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-display font-bold text-base">{c.name}</p>
                      {c.is_suspended && (
                        <span className="badge-pill text-[10px] bg-yellow-500/15 text-yellow-400 border border-yellow-500/30">
                          suspended
                        </span>
                      )}
                      {c.creator_is_active === false && (
                        <span className="badge-pill text-[10px] bg-red-500/15 text-red-400 border border-red-500/30">
                          creator suspended
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-white/40 mt-2">
                      Creator: <span className="text-white/70 break-all">{c.creator_email || "—"}</span>
                      {c.creator_name ? <> ({c.creator_name})</> : null}
                      {typeof c.approved_count === "number" ? <> · {c.approved_count} approved / {c.movies_count} total</> : null}
                      {c.created_at ? <> · created {new Date(c.created_at).toLocaleDateString()}</> : null}
                    </p>
                    {c.description && <p className="text-sm text-white/60 mt-2 line-clamp-2">{c.description}</p>}
                    {c.suspend_reason && <p className="text-xs text-yellow-300 mt-1">Suspended: {c.suspend_reason}</p>}
                  </div>
                  <div className="flex gap-2 flex-wrap shrink-0">
                    <Link to={`/channel/${c.id}`} className="ghost-btn !py-2 !px-4 text-sm" data-testid={`admin-channel-view-${c.id}`}>
                      View
                    </Link>
                    {c.is_suspended ? (
                      <button
                        onClick={() => unsuspendChannel(c.id)}
                        className="ghost-btn !py-2 !px-4 text-sm hover:border-green-500/50 hover:text-green-400"
                        data-testid={`unsuspend-channel-${c.id}`}
                      >
                        <ShieldCheck className="h-4 w-4" /> Unsuspend
                      </button>
                    ) : (
                      <button
                        onClick={() => setSoftAction({
                          kind: "channel.suspend", id: c.id,
                          title: `Suspend channel "${c.name}"?`,
                          description: <>
                            <p>The channel and all its movies are hidden from the public. The creator can still sign in but cannot upload new movies until you unsuspend.</p>
                          </>,
                          confirmButtonLabel: "Suspend channel",
                          successMsg: "Channel suspended",
                        })}
                        className="ghost-btn !py-2 !px-4 text-sm hover:border-yellow-500/50 hover:text-yellow-400"
                        data-testid={`suspend-channel-${c.id}`}
                      >
                        <ShieldOff className="h-4 w-4" /> Suspend
                      </button>
                    )}
                    <button
                      onClick={() => setHardDelete({
                        kind: "channel", id: c.id, confirmText: c.name, label: "channel name",
                        description: <>
                          <p><b>Permanently deletes</b> the channel &quot;<b>{c.name}</b>&quot; and all {c.movies_count} movie{c.movies_count === 1 ? "" : "s"} under it, including Cloudflare Stream slots and any pending payout requests. The creator account remains.</p>
                          <p className="text-yellow-400">This <b>cannot be undone</b>.</p>
                        </>,
                      })}
                      className="ghost-btn !py-2 !px-4 text-sm hover:border-red-500/50 hover:text-red-400"
                      data-testid={`hard-delete-channel-${c.id}`}
                    >
                      <Trash2 className="h-4 w-4" /> Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Appeals tab */}
      {tab === "appeals" && (
        <div className="space-y-4 mt-6" data-testid="admin-appeals-content">
          {appeals.length === 0 ? (
            <div className="card-surface p-12 text-center text-white/50" data-testid="admin-appeals-empty">
              <MessageSquare className="h-8 w-8 mx-auto text-white/20 mb-3" />
              No appeals yet. When a creator disputes a takedown, they&apos;ll show up here.
            </div>
          ) : appeals.map((a) => (
            <div key={a.id} className="card-surface p-5" data-testid={`admin-appeal-${a.id}`}>
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-display font-bold text-base">{a.movie_title}</p>
                    {a.status === "pending" && (
                      <span className="badge-pill text-[10px] bg-yellow-500/15 text-yellow-400 border border-yellow-500/30 inline-flex items-center gap-1">
                        <Clock className="h-3 w-3" /> pending
                      </span>
                    )}
                    {a.status === "approved" && (
                      <span className="badge-pill text-[10px] bg-green-500/15 text-green-400 border border-green-500/30 inline-flex items-center gap-1">
                        <CheckCircle2 className="h-3 w-3" /> restored
                      </span>
                    )}
                    {a.status === "dismissed" && (
                      <span className="badge-pill text-[10px] bg-red-500/15 text-red-400 border border-red-500/30 inline-flex items-center gap-1">
                        <XCircle className="h-3 w-3" /> dismissed
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-white/40 mt-2">
                    From <span className="text-white/70">{a.creator_email}</span>
                    {a.channel_name ? <> · {a.channel_name}</> : null}
                    {a.created_at ? <> · {new Date(a.created_at).toLocaleString()}</> : null}
                  </p>
                  {a.remove_reason && (
                    <p className="text-xs text-white/50 mt-2">
                      <span className="text-white/40">Original takedown reason:</span> {a.remove_reason}
                    </p>
                  )}
                  <div className="mt-3 bg-white/5 rounded-lg p-3 border-l-2 border-[#E65F2B]/40">
                    <p className="text-xs text-[#E65F2B] mb-1">Creator&apos;s appeal</p>
                    <p className="text-sm whitespace-pre-wrap">{a.message}</p>
                  </div>
                  {a.admin_note && (
                    <div className={`mt-3 rounded-lg p-3 border-l-2 ${a.status === "approved" ? "bg-green-500/5 border-green-500/40" : "bg-red-500/5 border-red-500/40"}`}>
                      <p className={`text-xs mb-1 ${a.status === "approved" ? "text-green-400" : "text-red-400"}`}>Your decision note</p>
                      <p className="text-sm whitespace-pre-wrap">{a.admin_note}</p>
                    </div>
                  )}
                </div>
                {a.status === "pending" && (
                  <div className="flex gap-2 shrink-0 flex-wrap">
                    <Link to={`/movie/${a.movie_id}`} className="ghost-btn !py-2 !px-4 text-sm" data-testid={`appeal-preview-${a.id}`}>
                      Preview movie
                    </Link>
                    <button
                      onClick={() => { setDecidingAppeal({ kind: "approve", appeal: a }); setAppealNote(""); }}
                      className="ember-btn !py-2 !px-4 text-sm bg-green-600 hover:bg-green-500"
                      data-testid={`appeal-approve-${a.id}`}
                    >
                      <CheckCircle2 className="h-4 w-4" /> Approve & restore
                    </button>
                    <button
                      onClick={() => { setDecidingAppeal({ kind: "dismiss", appeal: a }); setAppealNote(""); }}
                      className="ghost-btn !py-2 !px-4 text-sm hover:border-red-500/50 hover:text-red-400"
                      data-testid={`appeal-dismiss-${a.id}`}
                    >
                      <XCircle className="h-4 w-4" /> Dismiss
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Audit log tab */}
      {/* Genres tab */}
      {tab === "genres" && (
        <div className="space-y-6 mt-6" data-testid="admin-genres-content">
          <div className="card-surface p-6 sm:p-8 max-w-2xl">
            <div className="flex items-start gap-4">
              <div className="h-11 w-11 rounded-lg flex items-center justify-center shrink-0 bg-[#E65F2B]/15 border border-[#E65F2B]/30">
                <Film className="h-5 w-5 text-[#E65F2B]" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-display text-2xl font-bold">Movie genres</h3>
                <p className="text-sm text-white/60 mt-1">
                  These are the genre tags creators choose from when uploading. Movies can carry up to 3 genres. Renaming cascades to existing titles; deleting only works when nothing references the genre.
                </p>
              </div>
            </div>
            <form onSubmit={createGenre} className="mt-6 flex gap-2" data-testid="genre-create-form">
              <input
                value={newGenreName}
                onChange={(e) => setNewGenreName(e.target.value)}
                placeholder="New genre (e.g. 'Testimony', 'Bible Study')"
                className="input-dark flex-1"
                maxLength={60}
                disabled={genreBusy}
                data-testid="genre-name-input"
              />
              <button
                type="submit"
                disabled={genreBusy || !newGenreName.trim()}
                className="ember-btn disabled:opacity-40"
                data-testid="genre-create-submit"
              >
                Add genre
              </button>
            </form>
          </div>

          <div>
            <h4 className="font-display text-lg font-bold mb-3">{genres.length} genre{genres.length === 1 ? "" : "s"}</h4>
            {genres.length === 0 ? (
              <div className="card-surface p-10 text-center text-white/50" data-testid="admin-genres-empty">
                No genres yet — add one above.
              </div>
            ) : (
              <div className="space-y-2" data-testid="admin-genres-list">
                {genres.map((g) => (
                  <div key={g.id} className="card-surface p-4" data-testid={`genre-row-${g.slug}`}>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                      <div className="min-w-0 flex-1">
                        {editingGenreId === g.id ? (
                          <input
                            value={editingGenreName}
                            onChange={(e) => setEditingGenreName(e.target.value)}
                            autoFocus
                            maxLength={60}
                            className="input-dark"
                            onKeyDown={(e) => { if (e.key === "Escape") cancelEditGenre(); if (e.key === "Enter") saveEditGenre(); }}
                            data-testid={`genre-edit-input-${g.slug}`}
                          />
                        ) : (
                          <>
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-medium">{g.name}</p>
                              {(g.tagline || g.intro) && (
                                <span className="badge-pill text-[10px] bg-[#E65F2B]/15 text-[#E65F2B] border border-[#E65F2B]/30">
                                  <Sparkles className="h-3 w-3 mr-1" />Shelf
                                </span>
                              )}
                            </div>
                            <a href={`/genres/${g.slug}`} target="_blank" rel="noreferrer" className="text-xs text-white/40 hover:text-white/70 mt-1 font-mono inline-block transition">
                              /genres/{g.slug} ↗
                            </a>
                          </>
                        )}
                      </div>
                      <div className="flex gap-2 shrink-0 flex-wrap">
                        {editingGenreId === g.id ? (
                          <>
                            <button onClick={saveEditGenre} disabled={genreBusy || !editingGenreName.trim()} className="ghost-btn !py-2 !px-4 text-sm hover:border-green-500/50 hover:text-green-400 disabled:opacity-40" data-testid={`genre-save-${g.slug}`}>
                              <CheckCircle2 className="h-4 w-4" /> Save
                            </button>
                            <button onClick={cancelEditGenre} className="ghost-btn !py-2 !px-4 text-sm" data-testid={`genre-cancel-${g.slug}`}>
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button onClick={() => openShelfEditor(g)} className="ghost-btn !py-2 !px-4 text-sm hover:border-[#E65F2B]/50 hover:text-[#E65F2B]" data-testid={`genre-shelf-${g.slug}`}>
                              <Sparkles className="h-4 w-4" /> Shelf copy
                            </button>
                            <button onClick={() => startEditGenre(g)} className="ghost-btn !py-2 !px-4 text-sm" data-testid={`genre-edit-${g.slug}`}>
                              <Type className="h-4 w-4" /> Rename
                            </button>
                            <button onClick={() => deleteGenre(g)} className="ghost-btn !py-2 !px-4 text-sm hover:border-red-500/50 hover:text-red-400" data-testid={`genre-delete-${g.slug}`}>
                              <Trash2 className="h-4 w-4" /> Delete
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Inline shelf-copy editor */}
                    {editingShelfId === g.id && (
                      <div className="mt-5 pt-5 border-t border-white/5 space-y-4" data-testid={`shelf-editor-${g.slug}`}>
                        <div>
                          <label className="text-eyebrow mb-2 block">Tagline · shown as the H1 on /genres/{g.slug}</label>
                          <input
                            value={shelfTagline}
                            onChange={(e) => setShelfTagline(e.target.value)}
                            placeholder={`${g.name} films, made in faith.`}
                            maxLength={200}
                            className="input-dark"
                            disabled={genreBusy}
                            data-testid={`shelf-tagline-${g.slug}`}
                          />
                          <p className="text-xs text-white/30 mt-1">{shelfTagline.length} / 200 · also used as the page meta description.</p>
                        </div>
                        <div>
                          <label className="text-eyebrow mb-2 block">Intro paragraph · shown below the tagline</label>
                          <textarea
                            value={shelfIntro}
                            onChange={(e) => setShelfIntro(e.target.value)}
                            placeholder={`Tell viewers what makes ${g.name.toLowerCase()} films on Christoreel worth their time...`}
                            rows={4}
                            maxLength={2000}
                            className="input-dark resize-none"
                            disabled={genreBusy}
                            data-testid={`shelf-intro-${g.slug}`}
                          />
                          <p className="text-xs text-white/30 mt-1">{shelfIntro.length} / 2000 · supports line breaks for paragraphs.</p>
                        </div>
                        <div className="flex gap-2">
                          <button onClick={saveShelfCopy} disabled={genreBusy} className="ember-btn disabled:opacity-40" data-testid={`shelf-save-${g.slug}`}>
                            <CheckCircle2 className="h-4 w-4" /> Save shelf copy
                          </button>
                          <button onClick={closeShelfEditor} className="ghost-btn !py-2 !px-4 text-sm" data-testid={`shelf-cancel-${g.slug}`}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "newsletter" && (
        <>
          <NotificationsPanel />
          <NewsletterAdmin />
        </>
      )}

      {tab === "audit" && (
        <div className="space-y-2 mt-6" data-testid="admin-audit-content">
          {auditLog.length === 0 ? (
            <div className="card-surface p-12 text-center text-white/50" data-testid="admin-audit-empty">
              <History className="h-8 w-8 mx-auto text-white/20 mb-3" />
              No admin actions logged yet.
            </div>
          ) : (
            <div className="card-surface overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-audit-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-white/40 border-b border-white/5">
                    <th className="px-4 py-3 font-medium">When</th>
                    <th className="px-4 py-3 font-medium">Actor</th>
                    <th className="px-4 py-3 font-medium">Action</th>
                    <th className="px-4 py-3 font-medium">Target</th>
                    <th className="px-4 py-3 font-medium">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLog.map((row) => (
                    <tr key={row._id} className="border-b border-white/5 last:border-b-0" data-testid={`audit-row-${row._id}`}>
                      <td className="px-4 py-3 align-top text-white/60 whitespace-nowrap">
                        {row.at ? new Date(row.at).toLocaleString() : "—"}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <span className="text-white/70 break-all">{row.actor_email || row.actor_id}</span>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <span className={`badge-pill text-[10px] border ${
                          row.action?.startsWith("user.delete") || row.action?.startsWith("channel.delete") || row.action?.startsWith("movie.delete")
                            ? "bg-red-500/15 text-red-400 border-red-500/30"
                            : row.action?.endsWith(".unsuspend") || row.action === "movie.restore" || row.action === "appeal.approve"
                              ? "bg-green-500/15 text-green-400 border-green-500/30"
                              : "bg-yellow-500/15 text-yellow-400 border-yellow-500/30"
                        }`}>
                          {row.action}
                        </span>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <span className="text-white/50">{row.target_type}</span>
                        <span className="text-white/30 mx-1">·</span>
                        <span className="text-white/70 font-mono text-xs break-all">{row.target_id}</span>
                      </td>
                      <td className="px-4 py-3 align-top text-white/60 max-w-md break-words">
                        {row.details && Object.keys(row.details).length > 0
                          ? <pre className="text-[11px] whitespace-pre-wrap font-mono">{JSON.stringify(row.details, null, 0)}</pre>
                          : <span className="text-white/30">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Invites tab (App Owner only) */}
      {tab === "invites" && isAppOwner && (
        <div className="space-y-6 mt-6" data-testid="admin-invites-content">
          <div className="card-surface p-6 sm:p-8 max-w-2xl">
            <div className="flex items-start gap-4">
              <div className="h-11 w-11 rounded-lg flex items-center justify-center shrink-0 bg-[#E65F2B]/15 border border-[#E65F2B]/30">
                <UserPlus className="h-5 w-5 text-[#E65F2B]" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-display text-2xl font-bold">Invite a new admin</h3>
                <p className="text-sm text-white/60 mt-1">
                  As the App Owner, you can email a 7-day invite link. Anyone who accepts will become an admin with full moderation power.
                </p>
              </div>
            </div>
            <form onSubmit={sendInvite} className="mt-6 space-y-4" data-testid="invite-form">
              <div>
                <label className="text-eyebrow mb-2 block">Email</label>
                <input
                  type="email"
                  required
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="esther@filmmaker.org"
                  className="input-dark"
                  disabled={inviteBusy}
                  data-testid="invite-email-input"
                />
              </div>
              <div>
                <label className="text-eyebrow mb-2 block">Optional note (shown in the invite)</label>
                <textarea
                  value={inviteMessage}
                  onChange={(e) => setInviteMessage(e.target.value)}
                  rows={3}
                  maxLength={500}
                  placeholder="I'd love your help moderating new submissions..."
                  className="input-dark resize-none"
                  disabled={inviteBusy}
                  data-testid="invite-message-input"
                />
              </div>
              <button
                type="submit"
                disabled={inviteBusy || !inviteEmail.trim()}
                className="ember-btn disabled:opacity-40"
                data-testid="invite-send-button"
              >
                <Mail className="h-4 w-4" />
                {inviteBusy ? "Sending..." : "Send admin invite"}
              </button>
            </form>
          </div>

          <div>
            <h4 className="font-display text-lg font-bold mb-3">Pending invites</h4>
            {invites.length === 0 ? (
              <div className="card-surface p-10 text-center text-white/50" data-testid="admin-invites-empty">
                <Mail className="h-7 w-7 mx-auto text-white/20 mb-3" />
                No pending invites.
              </div>
            ) : (
              <div className="space-y-2">
                {invites.map((inv) => (
                  <div key={inv.id} className="card-surface p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid={`invite-row-${inv.id}`}>
                    <div className="min-w-0">
                      <p className="font-medium break-all">{inv.email}</p>
                      <p className="text-xs text-white/40 mt-1">
                        Invited by <span className="text-white/60">{inv.invited_by_email}</span>
                        {inv.created_at ? <> · {new Date(inv.created_at).toLocaleDateString()}</> : null}
                        {inv.expires_at ? <> · expires {new Date(inv.expires_at).toLocaleDateString()}</> : null}
                      </p>
                      {inv.message && <p className="text-xs text-white/50 mt-2 italic break-words">&ldquo;{inv.message}&rdquo;</p>}
                    </div>
                    <button
                      onClick={() => revokeInvite(inv)}
                      className="ghost-btn !py-2 !px-4 text-sm hover:border-red-500/50 hover:text-red-400 shrink-0"
                      data-testid={`invite-revoke-${inv.id}`}
                    >
                      <XCircle className="h-4 w-4" /> Revoke
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Appeal decision modal */}
      {decidingAppeal && (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="appeal-decision-modal">
          <div className="card-surface w-full max-w-lg p-6 sm:p-8">
            <div className="flex items-start gap-3">
              <div className={`h-11 w-11 rounded-lg flex items-center justify-center shrink-0 ${decidingAppeal.kind === "approve" ? "bg-green-500/15 border border-green-500/30" : "bg-red-500/15 border border-red-500/30"}`}>
                {decidingAppeal.kind === "approve" ? <CheckCircle2 className="h-5 w-5 text-green-400" /> : <XCircle className="h-5 w-5 text-red-400" />}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-display text-2xl font-bold">
                  {decidingAppeal.kind === "approve" ? "Approve appeal & restore movie?" : "Dismiss appeal?"}
                </h3>
                <p className="text-sm text-white/60 mt-2">
                  {decidingAppeal.kind === "approve"
                    ? <>The movie <b>&ldquo;{decidingAppeal.appeal.movie_title}&rdquo;</b> will be restored to the catalog and the creator will be emailed your note.</>
                    : <>The movie stays removed and the creator will be emailed your reasoning.</>}
                </p>
              </div>
            </div>
            <textarea
              autoFocus
              value={appealNote}
              onChange={(e) => setAppealNote(e.target.value)}
              rows={4}
              maxLength={1000}
              className="input-dark resize-none mt-5"
              placeholder={decidingAppeal.kind === "approve" ? "Note to the creator (optional)…" : "Reason for dismissing the appeal (recommended)…"}
              disabled={appealBusy}
              data-testid="appeal-note-input"
            />
            <p className="text-xs text-white/30 mt-1">{appealNote.length}/1000</p>
            <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <button onClick={() => setDecidingAppeal(null)} disabled={appealBusy} className="ghost-btn" data-testid="appeal-decision-cancel">Cancel</button>
              <button
                onClick={decideAppeal}
                disabled={appealBusy}
                className={`ember-btn disabled:opacity-40 ${decidingAppeal.kind === "approve" ? "bg-green-600 hover:bg-green-500" : "bg-red-600 hover:bg-red-500"}`}
                data-testid="appeal-decision-submit"
              >
                {appealBusy ? "Working..." : decidingAppeal.kind === "approve" ? "Approve & restore" : "Dismiss appeal"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Soft modal: suspend / soft-remove */}
      <ConfirmSuspendModal
        open={!!softAction}
        onClose={() => !modalBusy && setSoftAction(null)}
        onConfirm={runSoftAction}
        title={softAction?.title || ""}
        description={softAction?.description}
        confirmButtonLabel={softAction?.confirmButtonLabel || "Confirm"}
        busy={modalBusy}
        testIdPrefix={softAction?.kind === "user.suspend" ? "suspend-user-modal" : softAction?.kind === "channel.suspend" ? "suspend-channel-modal" : "remove-movie-modal"}
      />

      {/* Hard delete modal: typed confirmation */}
      <ConfirmDeleteModal
        open={!!hardDelete}
        onClose={() => !modalBusy && setHardDelete(null)}
        onConfirm={runHardDelete}
        title={`Permanently delete ${hardDelete?.kind || ""}`}
        description={hardDelete?.description}
        confirmText={hardDelete?.confirmText || ""}
        confirmLabel={hardDelete?.label || "value"}
        requireReason
        busy={modalBusy}
        testIdPrefix={`hard-delete-${hardDelete?.kind || "x"}-modal`}
      />

      {/* Cloudflare playback diagnosis modal */}
      {probeMovie && (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="cf-probe-modal">
          <div className="card-surface w-full max-w-3xl p-6 sm:p-8 relative max-h-[90vh] overflow-y-auto">
            <button onClick={() => setProbeMovie(null)} className="absolute top-4 right-4 text-white/40 hover:text-white" aria-label="Close" data-testid="cf-probe-close">
              <XCircle className="h-5 w-5" />
            </button>
            <div className="flex items-start gap-3">
              <div className="h-11 w-11 rounded-lg flex items-center justify-center shrink-0 bg-[#E65F2B]/15 border border-[#E65F2B]/30">
                <Stethoscope className="h-5 w-5 text-[#E65F2B]" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-display text-2xl font-bold leading-tight break-words">Diagnose &ldquo;{probeMovie.title}&rdquo;</h3>
                <p className="text-sm text-white/60 mt-1">Live view from Cloudflare + a server-side manifest probe.</p>
              </div>
            </div>

            {probeBusy && (
              <div className="mt-8 flex items-center gap-3 text-white/60 text-sm" data-testid="cf-probe-loading">
                <div className="h-5 w-5 rounded-full border-2 border-[#E65F2B] border-t-transparent animate-spin" />
                Probing Cloudflare…
              </div>
            )}

            {probeData && (
              <div className="mt-6 space-y-5" data-testid="cf-probe-data">
                {/* Diagnosis summary */}
                <div>
                  <p className="text-eyebrow mb-2">Diagnosis</p>
                  <ul className="space-y-1.5" data-testid="cf-probe-diagnosis">
                    {probeData.diagnosis.map((line, i) => {
                      const tone = line.startsWith("ERROR") ? "red"
                        : line.startsWith("BLOCKED") ? "red"
                        : line.startsWith("WARNING") ? "yellow"
                        : "green";
                      const colors = tone === "red" ? "bg-red-500/10 border-red-500/30 text-red-300"
                        : tone === "yellow" ? "bg-yellow-500/10 border-yellow-500/30 text-yellow-200"
                        : "bg-green-500/10 border-green-500/30 text-green-200";
                      return (
                        <li key={i} className={`text-sm rounded-md px-3 py-2 border ${colors}`}>{line}</li>
                      );
                    })}
                  </ul>
                </div>

                {/* CF metadata table */}
                {probeData.cloudflare_meta && (
                  <div>
                    <p className="text-eyebrow mb-2">Cloudflare metadata</p>
                    <div className="card-surface overflow-x-auto">
                      <table className="w-full text-xs">
                        <tbody>
                          {Object.entries(probeData.cloudflare_meta).map(([k, v], i) => (
                            <tr key={k} className={i % 2 === 0 ? "bg-white/[0.02]" : ""}>
                              <td className="px-4 py-2 text-white/40 font-mono uppercase tracking-wider w-1/3 align-top">{k}</td>
                              <td className="px-4 py-2 text-white/80 font-mono break-all align-top">
                                {v === null ? <span className="text-white/30">null</span>
                                 : typeof v === "boolean" ? (v ? <span className="text-green-400">true</span> : <span className="text-red-400">false</span>)
                                 : String(v)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Manifest probe */}
                {probeData.manifest_probe && (
                  <div>
                    <p className="text-eyebrow mb-2">Server-side HEAD probe</p>
                    <div className="card-surface p-4">
                      <pre className="text-[11px] text-white/80 whitespace-pre-wrap break-all font-mono">{JSON.stringify(probeData.manifest_probe, null, 2)}</pre>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="mt-6 flex justify-end">
              <button onClick={() => setProbeMovie(null)} className="ghost-btn" data-testid="cf-probe-done">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
